import { useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DurationParts, formatDurationParts, parseDurationText } from "@/lib/duration-text";
import { FieldDataType, FieldGroup } from "@/types/dynamic-detail";

type DurationGroupFieldProps = {
    group: FieldGroup;
    fields: FieldDataType[];
    form: UseFormReturn<FieldValues>;
    /** Form path of each member, as `DynamicFormField` would name it. */
    pathOf: (field: FieldDataType) => string;
    disabled?: boolean;
};

const UNITS: (keyof DurationParts)[] = ["days", "hours", "minutes"];

/**
 * The members of a `compose: "duration"` group, typed as one delay.
 *
 * Each member keeps its own number in the form — the server stores and sums them as it
 * always has — and is found by its `unit`, so nothing here names a field. The text box
 * holds what the author typed while they type it, and is rewritten in canonical form when
 * they leave it, which is when « 90 min » becomes « 1 hour 30 minutes ».
 */
export function DurationGroupField({ group, fields, form, pathOf, disabled }: DurationGroupFieldProps) {
    const memberOf = (unit: keyof DurationParts) => fields.find(field => field.unit === unit);
    const current = (): DurationParts =>
        Object.fromEntries(UNITS.map(unit => [unit, Number(memberOf(unit) ? form.getValues(pathOf(memberOf(unit)!)) : 0) || 0])) as DurationParts;

    const [text, setText] = useState(() => {
        const parts = current();
        return parts.days || parts.hours || parts.minutes ? formatDurationParts(parts) : "";
    });
    const reading = parseDurationText(text);

    const write = (next: string) => {
        setText(next);
        const parsed = parseDurationText(next);
        if (!parsed.ok) return;
        UNITS.forEach(unit => {
            const member = memberOf(unit);
            if (member) form.setValue(pathOf(member), parsed.parts[unit], { shouldDirty: true, shouldValidate: true });
        });
    };

    const id = `duration-${group.key}`;
    return (
        <div className="col-span-2 flex flex-col gap-1" data-testid={`field-group-${group.key}`}>
            <label htmlFor={id} className="text-sm font-medium">
                {group.label ?? "Duration"}
            </label>
            <Input
                id={id}
                value={text}
                disabled={disabled}
                placeholder="e.g. 1 day, 2 hours, 90 min, 1h30"
                aria-invalid={!reading.ok}
                className={cn(!reading.ok && "border-red-400")}
                onChange={event => write(event.target.value)}
                onBlur={() => reading.ok && setText(text.trim() === "" ? "" : formatDurationParts(reading.parts))}
            />
            <p className={cn("text-xs", reading.ok ? "text-gray-500" : "text-red-700")} data-testid={`${id}-reading`}>
                {reading.ok ? formatDurationParts(reading.parts) : reading.error}
            </p>
        </div>
    );
}
